import React, { useState } from 'react'
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material'
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts'
import SpeedIcon from '@mui/icons-material/Speed'
import GroupIcon from '@mui/icons-material/Group'
import BuildIcon from '@mui/icons-material/Build'
import StarIcon from '@mui/icons-material/Star'

interface MetricCard {
  title: string
  value: number
  unit: string
  change: number
  icon: React.ReactElement
  color: string
}

const OperationalMetrics: React.FC = () => {
  const [timeRange, setTimeRange] = useState<'week' | 'month' | 'quarter'>('month')

  const metrics: MetricCard[] = [
    {
      title: 'Customer Satisfaction',
      value: 91,
      unit: '%',
      change: 3.2,
      icon: <StarIcon />,
      color: '#4caf50'
    },
    {
      title: 'Technician Performance',
      value: 78,
      unit: '%',
      change: 5.1,
      icon: <GroupIcon />,
      color: '#2196f3'
    },
    {
      title: 'Work Order Turnaround',
      value: 4.2,
      unit: 'hours',
      change: -12.5,
      icon: <SpeedIcon />,
      color: '#ff9800'
    },
    {
      title: 'Parts Margin',
      value: 34,
      unit: '%',
      change: 1.8,
      icon: <BuildIcon />,
      color: '#9c27b0'
    }
  ]

  const performanceData = [
    { month: 'Jan', efficiency: 72, satisfaction: 88, utilization: 68 },
    { month: 'Feb', efficiency: 74, satisfaction: 89, utilization: 71 },
    { month: 'Mar', efficiency: 76, satisfaction: 90, utilization: 73 },
    { month: 'Apr', efficiency: 75, satisfaction: 89, utilization: 75 },
    { month: 'May', efficiency: 77, satisfaction: 91, utilization: 76 },
    { month: 'Jun', efficiency: 78, satisfaction: 91, utilization: 78 }
  ]

  const technicianData = [
    { name: 'John Smith', efficiency: 85, jobs: 142, satisfaction: 94, revenue: 52340 },
    { name: 'Mike Johnson', efficiency: 82, jobs: 138, satisfaction: 92, revenue: 48920 },
    { name: 'Sarah Williams', efficiency: 79, jobs: 125, satisfaction: 90, revenue: 45680 },
    { name: 'Tom Davis', efficiency: 76, jobs: 119, satisfaction: 88, revenue: 42150 },
    { name: 'Lisa Brown', efficiency: 74, jobs: 115, satisfaction: 87, revenue: 39870 }
  ]

  const serviceTypeData = [
    { type: 'Oil Change', count: 234, avgTime: 0.5, revenue: 11700 },
    { type: 'Brake Service', count: 156, avgTime: 2.5, revenue: 46800 },
    { type: 'Diagnostics', count: 189, avgTime: 1.5, revenue: 18900 },
    { type: 'Tire Service', count: 167, avgTime: 1.0, revenue: 25050 },
    { type: 'Major Repair', count: 89, avgTime: 4.5, revenue: 71200 }
  ]

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Operational Metrics
        </Typography>
        <ToggleButtonGroup
          value={timeRange}
          exclusive
          onChange={(_, value) => value && setTimeRange(value)}
          size="small"
        >
          <ToggleButton value="week">Week</ToggleButton>
          <ToggleButton value="month">Month</ToggleButton>
          <ToggleButton value="quarter">Quarter</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Key Metrics */}
      <Grid container spacing={3} mb={3}>
        {metrics.map((metric, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography color="textSecondary" gutterBottom variant="body2">
                      {metric.title}
                    </Typography>
                    <Typography variant="h4" component="h2">
                      {metric.value}{metric.unit}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: metric.change >= 0 ? 'success.main' : 'error.main',
                        mt: 1
                      }}
                    >
                      {metric.change >= 0 ? '+' : ''}{metric.change}% vs last {timeRange}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      backgroundColor: metric.color + '20',
                      borderRadius: '50%',
                      p: 1,
                      display: 'flex'
                    }}
                  >
                    {React.cloneElement(metric.icon, { sx: { color: metric.color } })}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Performance Trends */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Performance Trends
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={performanceData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="efficiency" stroke="#8884d8" name="Efficiency %" />
                  <Line type="monotone" dataKey="satisfaction" stroke="#82ca9d" name="Satisfaction %" />
                  <Line type="monotone" dataKey="utilization" stroke="#ffc658" name="Utilization %" />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Service Type Distribution
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={serviceTypeData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="type" type="category" width={80} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#8884d8" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Technician Performance */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Top Technician Performance
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Technician</TableCell>
                  <TableCell align="center">Efficiency</TableCell>
                  <TableCell align="center">Jobs Completed</TableCell>
                  <TableCell align="center">Customer Satisfaction</TableCell>
                  <TableCell align="right">Revenue Generated</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {technicianData.map((tech, index) => (
                  <TableRow key={index}>
                    <TableCell>{tech.name}</TableCell>
                    <TableCell align="center">
                      <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                        <LinearProgress
                          variant="determinate"
                          value={tech.efficiency}
                          sx={{ width: 60, height: 6 }}
                        />
                        <Typography variant="body2">{tech.efficiency}%</Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">{tech.jobs}</TableCell>
                    <TableCell align="center">
                      <Chip
                        label={`${tech.satisfaction}%`}
                        color={tech.satisfaction >= 90 ? 'success' : 'warning'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      ${tech.revenue.toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Operational Insights */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Key Opportunities
              </Typography>
              <Box display="flex" flexDirection="column" gap={2}>
                <Box>
                  <Typography variant="subtitle2" color="primary">
                    Schedule Optimization
                  </Typography>
                  <Typography variant="body2">
                    Implementing dynamic scheduling could reduce wait times by 18% and increase daily capacity by 12 vehicles
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="primary">
                    Parts Inventory
                  </Typography>
                  <Typography variant="body2">
                    Optimizing stock levels for top 50 parts could reduce carrying costs by $23K while maintaining 98% availability
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="primary">
                    Training Program
                  </Typography>
                  <Typography variant="body2">
                    Targeted training on diagnostic procedures could improve first-time fix rate from 82% to 91%
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Performance Benchmarks
              </Typography>
              <Box display="flex" flexDirection="column" gap={1}>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2">Average Repair Order</Typography>
                  <Typography variant="body2" fontWeight="bold">$485 (Industry: $425)</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2">Labor Efficiency</Typography>
                  <Typography variant="body2" fontWeight="bold">78% (Industry: 72%)</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2">Customer Retention</Typography>
                  <Typography variant="body2" fontWeight="bold">68% (Industry: 61%)</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2">Parts GP Margin</Typography>
                  <Typography variant="body2" fontWeight="bold">34% (Industry: 32%)</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2">Bay Utilization</Typography>
                  <Typography variant="body2" fontWeight="bold">73% (Industry: 68%)</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  )
}

export default OperationalMetrics